import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { AppDataState } from '../../../../common/state-stores/app-data-state.state';
import {
  WorldlineConfiguration,
  WorldlineFailedTransactionRequest,
  WorldlineFailedTransactionResponse,
  WorldlineInitResponse,
  WorldlinePaymentRequest,
  WorldlineProcessPaymentResponse,
} from '../../interfaces/worldline-payment';
import { PaymentTransaction } from '../../interfaces/payment-transaction';
import { RequestUtil } from '../../../../common/util/request-util';
import { ServiceErrorUtil } from '../../../../common/util/service-error.util';

@Injectable({
  providedIn: 'root',
})
export class WorldlinePaymentService {
  constructor(
    private httpClient: HttpClient,
    private appState: AppDataState,
  ) {}

  private buildHeaders(requestId: string): Record<string, string> {
    return {
      'App-Id': this.appState.getAppInstanceId().toString(),
      'Operator-Id': this.appState.getOperatorId().toString(),
      'PermissionSet-Id': this.appState.getPermissionSetId().toString(),
      'Request-Id': requestId,
    };
  }

  /**
   * Initialize Worldline payment session and get the merchant ID.
   */
  async initializePayment(): Promise<WorldlineInitResponse> {
    const requestId = RequestUtil.generateRequestId();
    try {
      const response = await lastValueFrom(
        this.httpClient.get<WorldlineInitResponse>(
          `${environment.baseBackendUrl}payment/worldline/init`,
          { headers: this.buildHeaders(requestId) },
        ),
      );

      if (!response?.merchantId) {
        throw ServiceErrorUtil.createServiceError(
          this.constructor.name,
          'Failed to initialize payment - incomplete response from server',
          requestId,
        );
      }

      return response;
    } catch (error: any) {
      throw ServiceErrorUtil.createServiceError(
        this.constructor.name,
        error.message || 'Payment initialization failed. Please try again.',
        requestId,
      );
    }
  }

  /**
   * Process Worldline payment with the hosted tokenization ID.
   */
  async processPayment(
    paymentRequest: WorldlinePaymentRequest,
  ): Promise<WorldlineProcessPaymentResponse> {
    const requestId = RequestUtil.generateRequestId();
    try {
      const response = await lastValueFrom(
        this.httpClient.post<WorldlineProcessPaymentResponse>(
          `${environment.baseBackendUrl}payment/worldline/process-payment`,
          paymentRequest,
          { headers: this.buildHeaders(requestId) },
        ),
      );

      if (!response?.success) {
        throw ServiceErrorUtil.createServiceError(
          this.constructor.name,
          response?.message || 'Payment processing failed',
          requestId,
        );
      }

      return response;
    } catch (error: any) {
      throw ServiceErrorUtil.createServiceError(
        this.constructor.name,
        error.message || 'Payment failed. Please try again.',
        requestId,
      );
    }
  }

  /**
   * Get Worldline payment configuration (admin).
   */
  async getConfiguration(): Promise<WorldlineConfiguration> {
    const requestId = RequestUtil.generateRequestId();
    try {
      const response = await lastValueFrom(
        this.httpClient.get<WorldlineConfiguration>(
          `${environment.baseBackendUrl}payment/worldline/configuration`,
          { headers: this.buildHeaders(requestId) },
        ),
      );

      if (!response) {
        throw ServiceErrorUtil.createServiceError(
          this.constructor.name,
          'Failed to retrieve Worldline configuration',
          requestId,
        );
      }

      return response;
    } catch (error: any) {
      throw ServiceErrorUtil.createServiceError(
        this.constructor.name,
        error.message || 'Failed to get configuration. Please try again.',
        requestId,
      );
    }
  }

  /**
   * Save Worldline payment configuration.
   */
  async saveConfiguration(
    configuration: WorldlineConfiguration,
  ): Promise<WorldlineConfiguration> {
    const requestId = RequestUtil.generateRequestId();
    try {
      const response = await lastValueFrom(
        this.httpClient.post<WorldlineConfiguration>(
          `${environment.baseBackendUrl}payment/worldline/configuration`,
          configuration,
          { headers: this.buildHeaders(requestId) },
        ),
      );

      if (!response) {
        throw ServiceErrorUtil.createServiceError(
          this.constructor.name,
          'Failed to save Worldline configuration',
          requestId,
        );
      }

      return response;
    } catch (error: any) {
      throw ServiceErrorUtil.createServiceError(
        this.constructor.name,
        error.message || 'Failed to save configuration. Please try again.',
        requestId,
      );
    }
  }

  /**
   * Get Worldline payment transactions.
   */
  async getTransactions(): Promise<PaymentTransaction[]> {
    const requestId = RequestUtil.generateRequestId();
    try {
      const response = await lastValueFrom(
        this.httpClient.get<PaymentTransaction[]>(
          `${environment.baseBackendUrl}payment/worldline/transactions`,
          { headers: this.buildHeaders(requestId) },
        ),
      );

      if (!response) {
        throw ServiceErrorUtil.createServiceError(this.constructor.name, 'Failed to retrieve payment transactions', requestId);
      }

      return response;
    } catch (error: any) {
      throw ServiceErrorUtil.createServiceError(
        this.constructor.name,
        error.message || 'Failed to get transactions. Please try again.',
        requestId,
      );
    }
  }

  /**
   * Register a failed Worldline payment transaction.
   */
  async registerFailedTransaction(
    request: WorldlineFailedTransactionRequest,
  ): Promise<WorldlineFailedTransactionResponse> {
    const requestId = RequestUtil.generateRequestId();
    try {
      const response = await lastValueFrom(
        this.httpClient.post<WorldlineFailedTransactionResponse>(
          `${environment.baseBackendUrl}payment/worldline/register-failed-transaction`,
          request,
          { headers: this.buildHeaders(requestId) },
        ),
      );

      if (!response?.success) {
        throw ServiceErrorUtil.createServiceError(
          this.constructor.name,
          response?.message || 'Failed to register failed transaction',
          requestId,
        );
      }

      return response;
    } catch (error: any) {
      throw ServiceErrorUtil.createServiceError(
        this.constructor.name,
        error.message || 'Failed to register failed transaction. Please try again.',
        requestId,
      );
    }
  }
}
